import * as React from 'react'
import { Button } from 'react-native'
import { StarXpandCommand } from 'react-native-star-io10'
import { Loader, Button as DSButton } from '../../../DesignSystem'
import { CutType } from 'react-native-star-io10/src/StarXpandCommand/Printer/CutType'
import { useState, useRef, useEffect } from 'react'
import ViewShot, { captureRef } from 'react-native-view-shot'
import { StartPrintReceiptView } from './StartPrintReceiptView.native'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { showAlert } from '../../../../state/Slices/settingsSlice'

type StarPrintReceiptProps = {
	prices?: any
	item: any
	printer: any
	automaticPrint?: boolean
	hidePrintButton?: boolean
	isOrderConfirmedModal?: boolean
}

// Add TextEncoder polyfill
if (typeof TextEncoder === 'undefined') {
	const { TextEncoder } = require('text-encoding')
	global.TextEncoder = TextEncoder
}

export default function StarPrintReceipt({
	prices,
	item,
	printer,
	automaticPrint = false,
	hidePrintButton = false,
	isOrderConfirmedModal = false,
}: StarPrintReceiptProps) {
	const dispatch = useAppDispatch()
	const { receiptPrinter } = useAppSelector(state => state.persisted)
	const { selectedMethods } = useAppSelector(state => state.checkout)
	const [isPrinting, setIsPrinting] = useState(false)
	const [isError, setIsError] = useState(false)
	const [isCancelled, setIsCancelled] = useState(false)
	const timeoutRef = useRef<NodeJS.Timeout>()
	const viewRef = useRef()

	useEffect(() => {
		if (isOrderConfirmedModal && selectedMethods.print) {
			console.log('PRINTING RECEIPT...')
			printReceipt()
		}
	}, [isOrderConfirmedModal, selectedMethods.print])

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	useEffect(() => {
		if (isCancelled && timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			setIsPrinting(false)
			setIsCancelled(false)
		}
	}, [isCancelled])

	const handlePrintReceipt = async () => {
		console.log('PRINTING RECEIPT...')
		await printReceipt()
	}

	const capture = async () => {
		const uri = await captureRef(viewRef, {
			format: 'png',
			quality: 1,
			result: 'data-uri',
		})
		return uri
	}

	const processUrl = url => {
		if (url) {
			return url.replace('data:image/png;base64,', '')
		} else {
			return null
		}
	}

	const handleCancelPrint = async () => {
		await printer.close()
		await printer.dispose()
		setIsPrinting(false)
		setIsCancelled(false)
	}

	const printReceipt = async () => {
		setIsError(false)
		setIsPrinting(true)
		setIsCancelled(false)
		timeoutRef.current = setTimeout(async () => {
			try {
				const url = await capture()
				if (isCancelled) {
					await handleCancelPrint()
					return
				}
				try {
					const builder = new StarXpandCommand.StarXpandCommandBuilder()
					builder.addDocument(
						new StarXpandCommand.DocumentBuilder().addPrinter(
							new StarXpandCommand.PrinterBuilder()
								.actionPrintImage(
									new StarXpandCommand.Printer.ImageParameter(`${processUrl(url)}`, 800)
								)
								.actionCut(CutType.Partial)
						)
					)

					var commands = await builder.getCommands()
					if (isCancelled) {
						await handleCancelPrint()
						return
					}

					await printer.open()
					if (isCancelled) {
						await handleCancelPrint()
						return
					}

					await printer.print(commands)
					console.log('Success')
				} catch (error) {
					console.log(`Error: ${String(error)}`)
					dispatch(
						showAlert({
							title: 'Printer Error',
							body: `Failed to connect to printer: ${String(error)}`,
							buttonA: 'Close',
						})
					)
					setIsError(true)
				} finally {
					await handleCancelPrint()
				}
			} catch (error) {
				console.log(`Error: ${String(error)}`)
				dispatch(
					showAlert({
						title: 'Printer Error',
						body: `Failed to capture receipt content: ${String(error)}`,
						buttonA: 'Close',
					})
				)
			}

			setIsPrinting(false)
			setIsCancelled(false)
		}, 1250)
	}

	if (global.kiosk) {
		return (
			<>
				<Loader
					visible={isPrinting && !isOrderConfirmedModal}
					message="Sending to printer..."
					actionButton={
						<DSButton fullWidth size="small" onPress={() => setIsCancelled(true)}>
							Cancel
						</DSButton>
					}
					style={{ width: 300 }}
				/>

				{!isOrderConfirmedModal && (
					<Button
						onPress={!printer ? null : handlePrintReceipt}
						color={!printer ? 'gray' : global.orgColor}
						title="PRINT RECEIPT"
					/>
				)}
				{printer && (
					<ViewShot ref={viewRef} style={{ position: 'absolute', left: 5000 }}>
						<StartPrintReceiptView
							prices={prices}
							orderData={item}
							automaticPrint={automaticPrint}
							isOrderConfirmedModal={isOrderConfirmedModal}
						/>
					</ViewShot>
				)}
			</>
		)
	} else {
		return null
	}
}
